import { P } from "@cd2/text"
import React from "react"
import { useGlobalState } from "./GlobalStateProvider"

export function BetaFeatureAccess({
  children,
  fallback,
  feature,
}: {
  children: React.ReactNode
  fallback?: React.ReactNode
  feature: keyof typeof defaultBetaFeatures
}) {
  const hasAccessToFeature = useBetaFeatureAccess()
  return (
    <React.Fragment>{hasAccessToFeature(feature) ? children : fallback ?? null}</React.Fragment>
  )
}

export function useBetaFeatureAccess() {
  const { currentUser } = useGlobalState()

  const featureOverride: Partial<IBetaFeatures> = {}

  const features = {
    ...defaultBetaFeatures,
    ...((currentUser.beta_features as any) ?? {}),
  } as IBetaFeatures

  function hasAccessToFeature(feature: keyof typeof defaultBetaFeatures) {
    // if (currentUser.isSuperAdmin) {
    //   return true
    // }
    return features[feature] || !!featureOverride[feature]
  }

  return hasAccessToFeature
}

export interface IBetaFeatures {
  useNewPriceListXls: boolean
  canViewNewWarehouseFeatures: boolean
  sageApi: boolean
  richQuoteEmail: boolean
  bomItems: boolean
  canEditSageProductLocations: boolean
  quoteRequests: boolean
  canAutoExportInternalOrders: boolean
  canConsolidateInternalOrders: boolean
  canExportStockTransfersViaApi: boolean
  canCreateAutomaticGoodsReceiptAllocations: boolean
  newDesign: boolean
}

export const defaultBetaFeatures: IBetaFeatures = {
  useNewPriceListXls: false,
  canViewNewWarehouseFeatures: false,
  sageApi: false,
  richQuoteEmail: false,
  bomItems: false,
  canEditSageProductLocations: false,
  quoteRequests: false,
  canAutoExportInternalOrders: false,
  canConsolidateInternalOrders: false,
  canExportStockTransfersViaApi: false,
  canCreateAutomaticGoodsReceiptAllocations: false,
  newDesign: false,
}

export const betaFeatureDescriptions: Record<keyof IBetaFeatures, React.ReactNode> = {
  useNewPriceListXls: <P>Allow downloading the new customer price list xlsx.</P>,
  canViewNewWarehouseFeatures: <P>Allow using the new warehouse features</P>,
  sageApi: <P>Allow using new sage API buttons</P>,
  richQuoteEmail: <P>Allow using the new rich quote email</P>,
  bomItems: <P>Allow using the new BOM items</P>,
  canEditSageProductLocations: <P>Allow using the new Product Location manager for products</P>,
  quoteRequests: <P>Allow using the quote requests for branches</P>,
  canAutoExportInternalOrders: (
    <P>Order requests that would generate an internal order are can be auto exported</P>
  ),
  canConsolidateInternalOrders: (
    <P>Allows for order requests that would generate an internal order to be consolidated</P>
  ),
  canExportStockTransfersViaApi: (
    <P>Allows the user to export stock transfers via the new api methods.</P>
  ),
  canCreateAutomaticGoodsReceiptAllocations: (
    <P>Allow creating automatic goods receipt allocations via the allocate drawer.</P>
  ),
  newDesign: (
    <P>
      Use the new, simpler page design where available (currently the purchase order page). Super
      admins can also switch this on and off from those pages.
    </P>
  ),
}
